/**
 * Helpers for the SEBI Filings & Approvals page (/sebi-filings-approvals),
 * backed by GET /api/core/ipos/sebi-filings-approvals.
 *
 * Each item is one offer document the tracker holds: a DRHP still with SEBI
 * (status "under_review") or an RHP, which a company can only file once SEBI
 * has issued its observations (status "approved"). The ids are the DRHP
 * tracker's, so every row opens the existing filing page.
 *
 * The two timestamps are when the backend picked each document up, not
 * SEBI's dates: "approval" routinely follows "filing" by minutes, where a real
 * SEBI review takes weeks. Rows carry them as the day a document reached the
 * tracker and the page labels them that way.
 */
import { istDate } from './drhpTracker';
import { matchKey } from './smeIpos';

export const SEBI_STATUS = {
  under_review: {
    label: 'Under SEBI Review',
    badge: 'bg-sky-50 text-sky-700 border-sky-200',
    note: 'DRHP filed, awaiting SEBI observations',
  },
  approved: {
    label: 'Approved · RHP Filed',
    badge: 'bg-teal-50 text-[#006666] border-teal-200',
    note: 'SEBI observations received, RHP on record',
  },
};

// A status the backend adds later still gets a readable badge
export const sebiStatusMeta = (status) =>
  SEBI_STATUS[status] || {
    label: String(status || 'Unknown').replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
    badge: 'bg-slate-100 text-slate-600 border-slate-200',
    note: '',
  };

export const DOC_BADGE = {
  DRHP: 'bg-sky-50 text-sky-700 border-sky-200',
  RHP: 'bg-[#008080] text-white border-[#008080]',
};

export const SEBI_TABS = [
  { key: 'all', label: 'All Filings', test: () => true },
  { key: 'under_review', label: 'Under SEBI Review', test: (row) => row.status === 'under_review' },
  { key: 'approved', label: 'Approved · RHP Filed', test: (row) => row.status === 'approved' },
];

export const sebiTabByKey = (key) => SEBI_TABS.find((tab) => tab.key === key) || SEBI_TABS[0];

const isApproved = (item) => item.status === 'approved' || item.document_type === 'RHP';

// Of a company's records: the one with a published analysis (its page has
// data), then the RHP-stage one, then the newest
const leadFirst = (a, b) =>
  Number(b.pipeline_status === 'approved') - Number(a.pipeline_status === 'approved') ||
  Number(isApproved(b)) - Number(isApproved(a)) ||
  String(b.filing_date || '').localeCompare(String(a.filing_date || ''));

const earliest = (dates) => dates.filter(Boolean).sort()[0] || null;

/**
 * One row per company, newest activity first.
 *
 * The feed can list a company twice -- an older record whose analysis is
 * published and a re-ingested copy still in review. Both describe the same
 * documents, so they fold into one row that links to the analysed record and
 * keeps the first day each document was seen.
 *
 * @param {object[]} items - response.data.items
 */
export const buildSebiRows = (items = []) => {
  const groups = new Map();
  items
    .filter((item) => item && item.id !== undefined && item.company_name)
    .forEach((item) => {
      const key = matchKey(item.company_name);
      groups.set(key, [...(groups.get(key) || []), item]);
    });

  return [...groups.values()]
    .map((group) => {
      const [lead] = [...group].sort(leadFirst);
      const approved = group.some(isApproved);
      const addedOn = earliest(group.map((item) => istDate(item.filing_date)));
      const rhpOn = approved ? earliest(group.filter(isApproved).map((item) => istDate(item.approval_date))) : null;
      return {
        id: String(lead.id),
        company: lead.company_name.replace(/\s+/g, ' ').trim(),
        docType: approved ? 'RHP' : 'DRHP',
        status: approved ? 'approved' : lead.status || 'under_review',
        board: ['mainboard', 'sme'].includes(lead.board) ? lead.board : null,
        exchange: typeof lead.listing_exchange === 'string' && lead.listing_exchange.trim() ? lead.listing_exchange.trim() : null,
        addedOn,
        rhpOn,
        analysisReady: group.some((item) => item.pipeline_status === 'approved'),
        detailPath: `/drhp-filings/${lead.id}`,
      };
    })
    .sort(
      (a, b) =>
        String(b.rhpOn || b.addedOn || '').localeCompare(String(a.rhpOn || a.addedOn || '')) ||
        a.company.localeCompare(b.company)
    );
};

export const filterSebiRows = (rows, { tab = 'all', query = '' } = {}) => {
  const needle = query.trim().toLowerCase();
  return rows
    .filter(sebiTabByKey(tab).test)
    .filter((row) => !needle || `${row.company} ${row.docType} ${row.exchange || ''}`.toLowerCase().includes(needle));
};

const DAY_MS = 86400000;

export const computeSebiStats = (rows, now = new Date()) => {
  // In IST, like the row dates it is compared with
  const weekAgo = istDate(new Date(now.getTime() - 7 * DAY_MS).toISOString());
  return {
    total: rows.length,
    underReview: rows.filter((row) => row.status === 'under_review').length,
    approved: rows.filter((row) => row.status === 'approved').length,
    analysed: rows.filter((row) => row.analysisReady).length,
    // Reached the tracker in the last seven days
    addedThisWeek: rows.filter((row) => row.addedOn && row.addedOn > weekAgo).length,
  };
};
